(function () {
  const storageKey = 'stellarPredictiveSearchDestination';
  const maxAge = 30 * 1000;

  /**
   * Send GA4 event.
   */
  function sendGa4Event(eventName, params) {
    window.dataLayer = window.dataLayer || [];

    if (typeof window.gtag !== 'function') {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }

    window.gtag('event', eventName, params);
  }

  /**
   * Normalize URL path.
   */
  function normalizePath(pathname) {
    const normalized = (pathname || '').replace(/\/+$/, '');

    return normalized || '/';
  }

  /**
   * Detect Shopify Search Results page.
   *
   * Search Results already has Shopify's standard
   * search event, so we do not create another custom
   * destination event for /search.
   */
  function isSearchResultsUrl(url) {
    const path = normalizePath(url.pathname);

    return path === '/search' || path.endsWith('/search');
  }

  /**
   * Check whether predictive results have actually rendered.
   */
  function hasPredictiveResults(predictiveSearch) {
    const resultsContainer = predictiveSearch.querySelector(
      '[data-predictive-search]'
    );

    if (!resultsContainer) return false;

    return Boolean(
      resultsContainer.querySelector('.predictive-search__item')
    );
  }

  /**
   * Fire search when predictive-search results become visible.
   *
   * IMPORTANT:
   * We fire when the result popup is rendered/open,
   * not on every input keystroke.
   */
  function trackPredictiveSearchPopup(predictiveSearch) {
    if (!predictiveSearch) return;

    const input = predictiveSearch.querySelector(
      'input[type="search"]'
    );

    if (!input) return;

    const searchTerm = input.value.trim();

    if (!searchTerm) return;

    /**
     * Dawn's predictive-search.js adds:
     *
     * results="true"
     * open
     * aria-expanded="true"
     *
     * after AJAX results have finished rendering.
     */
    const isOpen =
      predictiveSearch.hasAttribute('open') &&
      predictiveSearch.getAttribute('results') === 'true' &&
      input.getAttribute('aria-expanded') === 'true';

    if (!isOpen) return;

    if (!hasPredictiveResults(predictiveSearch)) {
      return;
    }

    /**
     * Avoid duplicate events caused by DOM mutations
     * for the same query while the popup remains open.
     */
    if (
      predictiveSearch.dataset.analyticsSearchOpen === 'true' &&
      predictiveSearch.dataset.analyticsSearchTerm === searchTerm
    ) {
      return;
    }

    predictiveSearch.dataset.analyticsSearchOpen = 'true';
    predictiveSearch.dataset.analyticsSearchTerm = searchTerm;

    sendGa4Event('search', {
      search_term: searchTerm,
      search_source: 'predictive_search'
    });
  }

  /**
   * Reset tracking when popup closes.
   *
   * This allows:
   *
   * search "Passion"
   * → popup opens
   * → search event
   *
   * close popup
   *
   * reopen "Passion"
   * → search event again
   */
  function resetPredictiveSearchPopup(predictiveSearch) {
    if (!predictiveSearch) return;

    const input = predictiveSearch.querySelector(
      'input[type="search"]'
    );

    const isOpen =
      predictiveSearch.hasAttribute('open') &&
      input?.getAttribute('aria-expanded') === 'true';

    if (isOpen) return;

    predictiveSearch.dataset.analyticsSearchOpen = 'false';
  }

  /**
   * Handle a predictive-search DOM/attribute update.
   */
  function handlePredictiveSearchChange(predictiveSearch) {
    resetPredictiveSearchPopup(predictiveSearch);

    trackPredictiveSearchPopup(predictiveSearch);
  }

  /**
   * Watch predictive search instances.
   *
   * predictive-search.js dynamically inserts AJAX HTML
   * into [data-predictive-search], then adds the "open"
   * and "results" attributes.
   */
  function observePredictiveSearch(predictiveSearch) {
    if (
      !predictiveSearch ||
      predictiveSearch.dataset.analyticsSearchObserved === 'true'
    ) {
      return;
    }

    predictiveSearch.dataset.analyticsSearchObserved = 'true';

    const resultsContainer = predictiveSearch.querySelector(
      '[data-predictive-search]'
    );

    /**
     * Watch:
     * - open attribute
     * - results attribute
     * - result HTML being rendered
     */
    const observer = new MutationObserver(function () {
      handlePredictiveSearchChange(predictiveSearch);
    });

    observer.observe(predictiveSearch, {
      attributes: true,
      attributeFilter: ['open', 'results'],
      childList: true,
      subtree: true
    });

    /**
     * Also watch aria-expanded on input because Dawn uses
     * it to represent popup open/closed state.
     */
    const input = predictiveSearch.querySelector(
      'input[type="search"]'
    );

    if (input) {
      const inputObserver = new MutationObserver(function () {
        handlePredictiveSearchChange(predictiveSearch);
      });

      inputObserver.observe(input, {
        attributes: true,
        attributeFilter: ['aria-expanded']
      });
    }

    /**
     * Handle an already-open cached predictive result.
     */
    if (resultsContainer) {
      handlePredictiveSearchChange(predictiveSearch);
    }
  }

  /**
   * Initialize all predictive-search components.
   */
  function initPredictiveSearchTracking() {
    document
      .querySelectorAll('predictive-search')
      .forEach(observePredictiveSearch);

    /**
     * In case a predictive-search component is added
     * dynamically later.
     */
    const pageObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (!(node instanceof Element)) {
            return;
          }

          if (
            node.matches &&
            node.matches('predictive-search')
          ) {
            observePredictiveSearch(node);
          }

          node
            .querySelectorAll?.('predictive-search')
            .forEach(observePredictiveSearch);
        });
      });
    });

    pageObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * When customer clicks a predictive-search result,
   * DO NOT fire search here.
   *
   * Only save search context so the destination page
   * can fire the event after navigation completes.
   */
  function savePredictiveSearchDestination(event) {
    if (
      event.defaultPrevented ||
      event.button > 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    if (!(event.target instanceof Element)) {
      return;
    }

    /**
     * Only links inside the rendered predictive-search results.
     */
    const link = event.target.closest(
      'predictive-search [data-predictive-search] a[href]'
    );

    if (!link || link.target === '_blank') {
      return;
    }

    const predictiveSearch = link.closest(
      'predictive-search'
    );

    if (!predictiveSearch) return;

    const input = predictiveSearch.querySelector(
      'input[type="search"]'
    );

    const searchTerm = input?.value.trim();

    if (!searchTerm) return;

    let destination;

    try {
      destination = new URL(
        link.href,
        window.location.origin
      );
    } catch (error) {
      return;
    }

    /**
     * Do not track external navigation.
     */
    if (
      destination.origin !==
      window.location.origin
    ) {
      return;
    }

    /**
     * "Search for XYZ" navigates to /search.
     *
     * Search Results already fires Shopify's standard
     * search event, so do not store another destination
     * event here or GA4 may receive duplicate searches.
     */
    if (isSearchResultsUrl(destination)) {
      return;
    }

    try {
      window.sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          searchTerm: searchTerm,
          destinationPath: normalizePath(
            destination.pathname
          ),
          createdAt: Date.now()
        })
      );
    } catch (error) {
      // Analytics must never block navigation.
    }
  }

  /**
   * Fire search after arriving on a PDP / CDP /
   * article / page selected from predictive search.
   */
  function firePendingPredictiveSearch() {
    let raw;

    try {
      raw = window.sessionStorage.getItem(
        storageKey
      );

      if (!raw) return;
    } catch (error) {
      return;
    }

    let pending;

    try {
      pending = JSON.parse(raw);
    } catch (error) {
      window.sessionStorage.removeItem(
        storageKey
      );
      return;
    }

    if (
      !pending?.searchTerm ||
      !pending?.destinationPath ||
      !pending?.createdAt
    ) {
      window.sessionStorage.removeItem(
        storageKey
      );
      return;
    }

    /**
     * Remove stale navigation context.
     */
    if (
      Date.now() - pending.createdAt >
      maxAge
    ) {
      window.sessionStorage.removeItem(
        storageKey
      );
      return;
    }

    /**
     * Only fire when we actually arrive at the
     * destination that was clicked.
     */
    if (
      normalizePath(window.location.pathname) !==
      pending.destinationPath
    ) {
      return;
    }

    window.sessionStorage.removeItem(
      storageKey
    );

    sendGa4Event('search', {
      search_term: pending.searchTerm,
      search_source:
        'predictive_search_destination'
    });
  }

  /**
   * Click listener DOES NOT send GA4 search.
   * It only stores navigation context.
   */
  document.addEventListener(
    'click',
    savePredictiveSearchDestination
  );

  function init() {
    initPredictiveSearchTracking();

    firePendingPredictiveSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );
  } else {
    init();
  }
})();